import type { ReactNode } from 'react';

import type { Permissions } from './registry';
import { usePermission } from './usePermission';

interface PermissionGateProps<Resource extends keyof Permissions> {
  /** Resource being protected (typed against the merged portal permissions). */
  resource: Resource;
  /** Action the user must be allowed to perform to see `children`. */
  action: Permissions[Resource]['action'];
  /** Optional resource instance for grants that depend on it (e.g. ownership). */
  data?: Permissions[Resource]['dataType'];
  /** Rendered when the user lacks the permission. Defaults to nothing. */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Declarative permission guard. Renders `children` only when the current user
 * is allowed to perform `action` on `resource`; otherwise renders `fallback`.
 */
export const PermissionGate = <Resource extends keyof Permissions>({
  resource,
  action,
  data,
  fallback = null,
  children,
}: PermissionGateProps<Resource>): ReactNode => {
  const { hasPermission } = usePermission();
  return hasPermission(resource, action, data) ? children : fallback;
};
